import {
	Checkbox,
	Group,
	Radio,
	Tooltip,
	createStyles,
	getStylesRef,
} from '@mantine/core';
import { Icon, ListBox } from '@repo/foundations';
import type { FilterItem } from './types';
import { FilterType } from './types';

const useStyles = createStyles(
	(theme, { selected }: { selected: boolean }) => ({
		menuItem: {
			[`&[data-hovered] .${getStylesRef('checkbox')}`]: {
				visibility: 'visible',
			},
			padding: theme.spacing['xs'],
			margin: `0 ${theme.spacing['2xs']}`,
			width: `calc(100% - (2 * ${theme.spacing['2xs']}))`,
		},
		checkbox: {
			ref: getStylesRef('checkbox'),

			visibility: selected ? 'visible' : 'hidden',
		},
	})
);

interface FilterMenuItemProps {
	filterType?: FilterType;
	item: FilterItem;
	renderMenuItem?: (item: FilterItem) => React.ReactNode;
	/**
	 * Callback invoked when the user selects/deselects this menu item. The
	 * boolean argument indicates whether the dropdown should close (true) or
	 * stay open (false).
	 */
	onClick: (close: boolean) => void;
	selected?: boolean;
	getProps: (
		userProps: Omit<React.HTMLProps<HTMLElement>, 'ref'>
	) => Omit<React.HTMLProps<HTMLElement>, 'ref'>;
}

export function FilterMenuItem({
	filterType,
	item,
	renderMenuItem,
	onClick,
	selected = false,
	getProps,
}: FilterMenuItemProps) {
	const { classes } = useStyles({ selected });

	// Ensure label is always a string (defensive check) e.g. {
	// "label": "Custom Property",
	// "value": [
	//     "Custom Property"
	// ] or {label: 'Is set', value: 'is_set'}
	const label =
		typeof item.label === 'string' ? item.label : String(item.label);

	return (
		<ListBox.Item
			{...getProps({
				onClick: () => onClick(filterType === FilterType.Single),
				className: classes.menuItem,
			})}
		>
			<Group
				noWrap
				data-testid={`filter-button-${label.replace(/\s/g, '-').toLowerCase()}`}
				spacing="2xs"
			>
				{filterType === FilterType.Multiple && (
					<Checkbox
						checked={selected}
						onClick={(event) => event.stopPropagation()}
						onChange={() => onClick(false)}
						className={classes.checkbox}
					/>
				)}
				{filterType === FilterType.Single && (
					<Radio
						checked={selected}
						onClick={(event) => event.stopPropagation()}
						onChange={() => onClick(false)}
						className={classes.checkbox}
					/>
				)}
				{item.icon}
				{renderMenuItem ? renderMenuItem(item) : label}
				{item.metadata &&
					typeof item.metadata === 'object' &&
					'isDeleted' in item.metadata &&
					item.metadata.isDeleted && (
						<Tooltip label={`This resource has been deleted`} position="right">
							<Icon name="infoCircleFilled" size="md" />
						</Tooltip>
					)}
			</Group>
		</ListBox.Item>
	);
}
